<template>
  <div
    :class="rootClasses"
    data-cy="ds-tabs"
  >
    <div :class="DS_TABS_CLASSES.ListWrapper">
      <div
        :class="DS_TABS_CLASSES.List"
        role="tablist"
        aria-orientation="horizontal"
        :aria-label="props.ariaLabel"
        data-cy="ds-tabs__list"
        @keydown="handleListKeydown"
      >
        <DSTab
          v-for="item in props.items"
          :key="item.value"
          :ref="element => setTabRef(item.value, element)"
          :item="item"
          :active="isSelected(item)"
          :disabled="isItemDisabled(item)"
          :direction="props.direction"
          :variant="props.variant"
          :tab-index="getItemTabIndex(item)"
          @select="selectItem"
        />
      </div>

      <div
        v-if="$slots.suffix"
        :class="DS_TABS_CLASSES.Suffix"
      >
        <slot name="suffix" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from "vue";
import { useStateClasses } from "@/src/design_system/shared/composables/useStateClasses";
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import DSTab from "./DSTab.vue";
import { useTabsKeyboardNavigation } from "../composables/useTabsKeyboardNavigation";
import { useTabsSelection } from "../composables/useTabsSelection";
import { DS_TABS_DIRECTION_TO_CLASS_MAP, DS_TABS_VARIANT_TO_CLASS_MAP, DS_TABS_CLASSES } from "../constants/classes";
import { DS_TABS_VARIANT, type DSTabsProps } from "../types/props";
import type { DSTabsEmits } from "../types/emits";

const props = withDefaults(defineProps<DSTabsProps>(), {
  modelValue: undefined,
  direction: DS_DIRECTION.Horizontally,
  variant: DS_TABS_VARIANT.Default,
  disabled: false,
  ariaLabel: "Tabs",
});

const emit = defineEmits<DSTabsEmits>();

defineSlots<{
  suffix?: () => unknown;
}>();

const { stateClasses } = useStateClasses(props);
const tabsItems = toRef(props, "items");

const rootClasses = computed(() => [
  {
    [DS_TABS_CLASSES.Root]: true,
    ...stateClasses.value,
  },
  DS_TABS_DIRECTION_TO_CLASS_MAP[props.direction],
  DS_TABS_VARIANT_TO_CLASS_MAP[props.variant],
]);

const { enabledItems, getItemTabIndex, isItemDisabled, isSelected, selectItem } = useTabsSelection({
  props,
  onSelect: item => {
    emit("update:modelValue", item.value);
    emit("tab:select", item);
  },
});

const { handleListKeydown, setTabRef } = useTabsKeyboardNavigation({
  enabledItems,
  isSelected,
  items: tabsItems,
  selectItem,
});
</script>
